import {
  loginConsumerLoan,
  loginDepositAccount,
  loginEmergencyFoundsOrinary,
  loginFixedDeposit3Months,
  loginFixedDeposit6Months,
  loginHighInterestAccount,
  loginHighInterestAccountWealth,
  loginPlacementAccountOrdinary,
  loginSavingsAccount,
  loginSmartPension,
  loginTasksDefault,
  loginTasksFram,
  loginTasksStandardBank,
} from '@features/LoginPage/loginPageOptions';
import { type LoginPageOptions } from '@features/LoginPage/types';

import { AccountType } from '../types/createCustomer';
import { Product } from '../types/enums';

import { addParamsToPath, routes } from './route.constants';

export const CUSTOMER_MASTER_TRIGGER_CLIENT = 'BNKNEWCUST';

export const externalProducts = [Product.DEPOSIT_ACCOUNT, Product.CONSUMER_LOAN];

export const AGE_REQUIREMENT_FOR_REGULAR_BANKING_PRODUCTS = 18;

interface UidAndProduct {
  uid: string;
  product: string;
}

interface ProductMapProps {
  defaultAccountType: AccountType | null;
  loginPageOptions: LoginPageOptions;
  firstProductRoute?: (uidAndProduct: UidAndProduct) => string;
}

const defaultHeader = 'loginPage.header';

export const productMap: Record<Product, ProductMapProps> = {
  [Product.SMART_PENSION]: {
    defaultAccountType: AccountType.SMART_ACCOUNT,
    loginPageOptions: loginSmartPension,
  },
  [Product.DEPOSIT_ACCOUNT]: {
    defaultAccountType: null,
    loginPageOptions: loginDepositAccount,
  },
  [Product.CONSUMER_LOAN]: {
    defaultAccountType: null,
    loginPageOptions: loginConsumerLoan,
  },
  [Product.FIXED_DEPOSIT_3_MONTHS]: {
    defaultAccountType: AccountType.DEPOSIT_ACCOUNT_FIXED_RATE_3_MONTH,
    loginPageOptions: loginFixedDeposit3Months,
  },
  [Product.FIXED_DEPOSIT_6_MONTHS]: {
    defaultAccountType: AccountType.DEPOSIT_ACCOUNT_FIXED_RATE,
    loginPageOptions: loginFixedDeposit6Months,
  },
  [Product.HIGH_INTEREST_ACCOUNT]: {
    defaultAccountType: AccountType.SAVINGS_ACCOUNT_HIGH_INTEREST_RATE_WITHOUT_CAMPAIGN,
    loginPageOptions: loginHighInterestAccount,
  },
  [Product.PLACEMENT_ACCOUNT_ORDINARY]: {
    defaultAccountType: AccountType.PLACEMENT_ACCOUNT_ORDINARY,
    loginPageOptions: loginPlacementAccountOrdinary,
  },
  [Product.EMERGENCY_FUNDS_ORDINARY]: {
    defaultAccountType: AccountType.EMERGENCY_FUNDS_ORDINARY,
    loginPageOptions: loginEmergencyFoundsOrinary,
  },
  [Product.HIGH_INTEREST_ACCOUNT_WEALTH]: {
    defaultAccountType: AccountType.HIGH_INTEREST_ACCOUNT_WEALTH,
    loginPageOptions: loginHighInterestAccountWealth,
  },
  [Product.SAVINGS_ACCOUNT]: {
    defaultAccountType: AccountType.SAVINGS_ACCOUNT,
    loginPageOptions: loginSavingsAccount,
  },
  [Product.FRAM]: {
    defaultAccountType: null,
    loginPageOptions: { tasks: loginTasksFram, header: 'loginPage.fram.header' },
    firstProductRoute: (uidAndProduct: UidAndProduct) =>
      addParamsToPath(routes.ProductOverviewFramMembership, uidAndProduct),
  },
  [Product.HIGH_INTEREST_ACCOUNT_HUSEIERNE]: {
    defaultAccountType: AccountType.SAVINGS_ACCOUNT_HUSEIERNE,
    loginPageOptions: { tasks: loginTasksDefault, header: defaultHeader },
    firstProductRoute: (uidAndProduct: UidAndProduct) =>
      addParamsToPath(routes.ProductOverviewHighInterestAccountHuseierne, uidAndProduct),
  },
  [Product.STANDARD_BANK]: {
    defaultAccountType: null,
    loginPageOptions: { tasks: loginTasksStandardBank, header: 'loginPage.bankAccount.header' },
    firstProductRoute: (uidAndProduct: UidAndProduct) =>
      addParamsToPath(routes.CardAndAccountStandardPage, uidAndProduct),
  },
  [Product.PRIVATE_BANKING]: {
    defaultAccountType: null,
    loginPageOptions: { tasks: loginTasksDefault, header: 'loginPage.privateBanking.header' },
    firstProductRoute: (uidAndProduct: UidAndProduct) =>
      addParamsToPath(routes.ProductOverviewPrivateBankingIntro, uidAndProduct),
  },
} as const;
